import { useState } from 'react'

import { BlankSlate, Box, Button, SearchField, Title } from '@factorialco/deprecated-design-system'
import {
  AccountingVendorPageDocument,
  LegalEntityWithFinanceAccountsFragment,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import Filter from 'modules/core/components/Filter'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { VendorRow } from 'modules/finance/components/Accounting/Vendor/VendorRow'
import { contactFullName } from 'modules/finance/lib/contactsUtils'
import PageLoading from 'modules/layout/components/Page/Loading'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export const NoAccountsBlankSlate = () => {
  const { navigation } = useNavigation()

  return (
    <Box flexGrow justifyContent="center">
      <BlankSlate
        illustration="alert"
        size="small"
        title={i18n.t('finance.settings.accounting.expense_category.blank_slate.title')}
        description={i18n.t('finance.settings.accounting.expense_category.blank_slate.description')}
      >
        <Button.Router to={navigation.finance.accounting.fullPath()}>
          {i18n.t('finance.settings.accounting.expense_category.blank_slate.cta')}
        </Button.Router>
      </BlankSlate>
    </Box>
  )
}

const Vendor = () => {
  const { search, onSearch } = useDebouncedSearch()
  const [legalEntity, setLegalEntity] = useState<LegalEntityWithFinanceAccountsFragment>()
  const [isFetching, setIsFetching] = useState(true)

  const { company } = useCurrentSession()

  const { data, loading, error } = useQuery(AccountingVendorPageDocument, {
    variables: { companyId: company.id, legalEntityId: legalEntity?.id ?? 0 },
  })
  // TODO: VendorsPage should let you assign sale and purchase ledger accounts so we can remove this
  const canSeePurchaseLedgerAccountSettings =
    data?.finance?._policies?.journalEntriesManagePurchase?.can ?? false
  const canManage = data?.finance?._policies?.accountingManageAccountResources?.can ?? false
  const legalEntities = data?.companies?.legalEntities ?? []
  const vendors = data?.finance?.contacts ?? []

  useUnsafeEffect(() => {
    if (legalEntity) return
    setLegalEntity(legalEntities?.find((node) => node.main))
    setIsFetching(false)
  }, [legalEntities, legalEntity])

  const legalEntityOptions = useUnsafeMemo(
    () => legalEntities.map((le) => ({ label: le.legalName, value: le })),
    [legalEntities]
  )

  const accounts = legalEntity?.financeAccounts ?? []
  const filteredVendors = sortBy(
    vendors.filter((vendor) =>
      contactFullName(vendor).toLowerCase().includes(search.toLowerCase())
    ),
    ['legalName']
  )

  return (
    <ResponsivePage.Body
      loading={loading || isFetching}
      error={error}
      authorized={canSeePurchaseLedgerAccountSettings}
    >
      <Box height="full">
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" paddingTop="s24">
          <>
            <Filter.SingleChoice
              label={i18n.t('finance.workspace.legal_entity_scope.selector_label')}
              options={legalEntityOptions}
              selectedOption={{ label: legalEntity?.legalName ?? '', value: legalEntity }}
              onValueChange={setLegalEntity}
              noFooter
            />
          </>
          {accounts.length > 0 && (
            <Box width="fit-content">
              <SearchField
                variant="regular"
                placeholder={i18n.t('common.search')}
                search={search}
                onSearch={onSearch}
              />
            </Box>
          )}
        </Box>

        {accounts.length === 0 &&
          (loading || isFetching ? (
            <Box flexGrow justifyContent="center">
              <PageLoading />
            </Box>
          ) : (
            <NoAccountsBlankSlate />
          ))}

        {accounts.length > 0 && legalEntity && (
          <>
            <Box
              flexDirection="row"
              gap="s16"
              justifyContent="spaceBetween"
              alignItems="flexStart"
              paddingTop="s64"
            >
              <Box width="s600">
                <Title title={i18n.t('finance.settings.accounting.vendor_title')}></Title>
              </Box>
              <Box width="full">
                <Title
                  title={i18n.t('finance.settings.accounting.assigned_purchase_accounts')}
                ></Title>
              </Box>
            </Box>

            <>
              {filteredVendors.map((vendor) => (
                <VendorRow
                  key={vendor.id}
                  accounts={accounts}
                  legalEntity={legalEntity}
                  vendor={vendor}
                  canManage={canManage}
                />
              ))}
            </>
          </>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default Vendor
